<template>
  <SettingsCard
    :title="$t('settings.crm.multi-company-parent.title')"
    :subtitle="$t('settings.crm.multi-company-parent.subtitle')"
    icon="mdi-account-group-outline"
    :model-value="isOpen"
    @update:model-value="isOpen = !isOpen"
  >
    <v-row class="mx-0 my-2 mx-md-2">
      <v-col cols="12">
        <v-table
          v-if="rows.length > 0"
          density="comfortable"
        >
          <thead>
            <tr>
              <th>{{ $t('settings.crm.multi-company-parent.company-column') }}</th>
              <th>{{ $t('settings.crm.multi-company-parent.team-column') }}</th>
              <th>{{ $t('labels.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.companyId"
            >
              <td>{{ row.name }}</td>
              <td>
                <span v-if="row.teamId">
                  {{ row.teamName ? `${row.teamName} (${row.teamId})` : row.teamId }}
                </span>
                <span
                  v-else
                  class="text-grey"
                  >{{ $t('settings.crm.multi-company-parent.no-team') }}</span
                >
              </td>
              <td>
                <PropstackChildTeamAssignmentDialog
                  :company-name="row.name"
                  :child-company-id="String(row.companyId)"
                  :team-id="row.teamId"
                  @saved="refetch"
                />
              </td>
            </tr>
          </tbody>
        </v-table>
        <v-alert
          v-else
          type="info"
          density="compact"
          variant="tonal"
        >
          {{ $t('settings.crm.multi-company-parent.no-children') }}
        </v-alert>
      </v-col>
    </v-row>
  </SettingsCard>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, inject, ref } from 'vue'

import SettingsCard from '@/components/SettingsCard.vue'
import CHILD_COMPANIES from '@/modules/company/queries/ChildCompanies.gql'

import PropstackChildTeamAssignmentDialog from './PropstackChildTeamAssignmentDialog.vue'
import PROPSTACK_CHILDREN_MULTI_COMPANY_CONFIG from './queries/PropstackChildrenMultiCompanyConfig.gql'
import PROPSTACK_TEAMS from './queries/PropstackTeams.gql'

const auth = inject<{ user?: { companyId: string } }>('auth')
const parentCompanyId = computed(() => auth?.user?.companyId ?? '')
const isOpen = ref(true)

const { result: teamsResult } = useQuery(PROPSTACK_TEAMS, () => ({
  companyId: parentCompanyId.value,
}))
const teamsMap = computed(() => {
  const map = new Map<string, string>()
  for (const team of teamsResult.value?.propstackTeams ?? []) {
    map.set(String(team.id), team.name ?? '')
  }
  return map
})

const { result: childConfigResult, refetch: refetchConfig } = useQuery(
  PROPSTACK_CHILDREN_MULTI_COMPANY_CONFIG,
  () => ({ companyId: parentCompanyId.value })
)
const enrolledChildren = computed(
  () => childConfigResult.value?.propstackChildrenMultiCompanyConfig ?? []
)

const { result: companiesResult } = useQuery(CHILD_COMPANIES)
const childCompanies = computed(() => companiesResult.value?.childCompanies ?? [])

const rows = computed(() => {
  const enrolledMap = new Map<string, string | null>(
    enrolledChildren.value.map((e: any) => [String(e.companyId), (e.teamId as string) ?? null])
  )
  return childCompanies.value.map((c: any) => {
    const teamId: string | null = enrolledMap.get(String(c.id)) ?? null
    return {
      companyId: c.id,
      name: c.name,
      teamId,
      teamName: teamId ? (teamsMap.value.get(teamId) ?? null) : null,
    }
  })
})

function refetch() {
  refetchConfig()
}
</script>
